import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DigitalEmployee } from '../types';

interface ChatInterfaceProps {
  employee: DigitalEmployee;
}

interface Message {
  sender: 'user' | 'employee';
  content: string;
  timestamp: Date;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ employee }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([
    {
      sender: 'employee',
      content: `Hello! I'm ${employee.name}. How can I help you today?`,
      timestamp: new Date()
    }
  ]);
  const [newMessage, setNewMessage] = useState('');

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    // Add user message
    const userMessage: Message = {
      sender: 'user',
      content: newMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');

    // Simulate AI response
    setTimeout(() => {
      const employeeResponses = [
        `Based on my ${employee.skills.join(', ')} skills, I can help with that!`,
        `That's an interesting question. Let me think about it...`,
        `I'd be happy to assist you with that task.`,
        `Could you provide more details so I can better help you?`
      ];

      const randomResponse = employeeResponses[Math.floor(Math.random() * employeeResponses.length)];

      const aiMessage: Message = {
        sender: 'employee',
        content: randomResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    }, 1000);
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <div className="chat-header-left">
          <div className="employee-avatar small">
            <img src={employee.avatar} alt={employee.name} />
          </div>
          <h3>Chat with <span className="text-gradient">{employee.name}</span></h3>
        </div>
        <button className="close-chat-button" onClick={() => navigate(-1)}>
          <span className="close-icon">×</span>
        </button>
      </div>

      <div className="chat-messages">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`message ${message.sender === 'user' ? 'user-message' : 'employee-message'}`}
          >
            {message.sender === 'employee' && (
              <div className="message-avatar">
                <img src={employee.avatar} alt={employee.name} />
              </div>
            )}
            <div className="message-content">
              <p>{message.content}</p>
              <span className="message-time">
                {message.timestamp.toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="chat-input">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
        />
        <button onClick={handleSendMessage}>Send</button>
      </div>
    </div>
  );
};

export default ChatInterface;
